import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// In-memory token store — not localStorage (XSS-safe), not cookies (avoids CORS wildcard issue)
let memoryToken = null;

// CRITICAL: Ensure credentials are NEVER sent — the K8s ingress returns CORS `*`,
// and browsers block responses to credentialed requests with a wildcard origin.
axios.defaults.withCredentials = false;

// Clear any stale interceptors from hot-reload cycles, then register ours
axios.interceptors.request.handlers = [];
axios.interceptors.request.use((config) => {
  config.withCredentials = false;
  if (memoryToken) {
    config.headers.Authorization = `Bearer ${memoryToken}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (!memoryToken) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      memoryToken = null;
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    memoryToken = data.access_token;
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (err) {
      // Best-effort: server session cleanup may fail if token is already expired.
      // Token is cleared client-side regardless, so the user is still logged out.
      void err;
    }
    memoryToken = null;
    setUser(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  const value = useMemo(
    () => ({ user, loading, login, logout, checkAuth }),
    [user, loading, login, logout, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
